import DashboardBlock from '../DashboardBlock/DashboardBlock';
import styles from './EmployeesDashboard.module.scss';
import { useTranslation } from 'react-i18next';

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';

import {
  IFormikValues,
  UserActivityParams,
} from './UserActivityParams/UserActivityParams';
import CloseItemIcon from '~/assets/svg/newSvg/close-bg-border.svg?react';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useState } from 'react';
import { FormikHelpers } from 'formik';
import { NewDropdown } from '~/components/Shared/NewDropdown/NewDropdown';
import { UserLineActivityChart } from './UserActivityChart/UserLineActivityChart';
import dayjs from 'dayjs';

ChartJS.register(ArcElement, Tooltip, Legend);

interface EmployesProps {
  startDate: string;
  endDate: string;
  assetsIds: number[];
  accommodationIds: number[];
  organizationIds: number[];
}

interface IUsersLoginData {
  chart: {
    [key: string]: {
      [key: string]: {
        ['1_day']: number;
        ['7_day']: number;
        ['30_day']: number;
        login_count: number;
      };
    };
  };
  total: {
    [key: string]: number;
  };
}

const EmployeesDashboard = ({
  startDate,
  endDate,
  assetsIds,
  accommodationIds,
  organizationIds,
}: EmployesProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const [isOpenDropdown, setIsOpenDropdown] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalLogin, setTotalLogin] = useState<number | null>(null);
  const [weekDays, setWeekDays] = useState({
    start: dayjs(startDate).format('YYYY-MM-DD'),
    end: dayjs(startDate).add(1, 'week').format('YYYY-MM-DD'),
  });
  const [formValues, setFormValues] = useState<IFormikValues>({
    entrance_quantity: [
      {
        id: '1',
        value: '1',
        condition: { id: 4, name: '>=', display_name: t('more_or_equal') },
      },
    ],
  });

  const handleWeekChange = (trigger: 'prev' | 'next') => {
    if (trigger === 'prev') {
      if (dayjs(weekDays.start).isAfter(dayjs(startDate))) {
        setWeekDays(prev => ({
          start: dayjs(prev.start).subtract(1, 'week').format('YYYY-MM-DD'),
          end: dayjs(prev.end).subtract(1, 'week').format('YYYY-MM-DD'),
        }));
      }
    } else if (trigger === 'next') {
      if (dayjs(weekDays.end).isBefore(dayjs(endDate))) {
        setWeekDays(prev => {
          const newEnd = dayjs(prev.end).add(1, 'week').format('YYYY-MM-DD');

          const isMoreThanWeek = dayjs(newEnd).diff(dayjs(endDate), 'day') > 7;

          return {
            start: dayjs(prev.start).add(1, 'week').format('YYYY-MM-DD'),
            end: isMoreThanWeek ? dayjs(endDate).format('YYYY-MM-DD') : newEnd,
          };
        });
      }
    }
  };

  const canGoPrev = dayjs(weekDays.start).isAfter(dayjs(startDate));
  const canGoNext = dayjs(weekDays.end).isBefore(dayjs(endDate));

  const userParams = {
    dates: [
      dayjs(startDate).format('YYYY-MM-DD'),
      dayjs(endDate).format('YYYY-MM-DD'),
    ],
    asset_ids: assetsIds,
    accommodation_ids: accommodationIds,
    organization_ids: organizationIds,
  };

  const loginActivityParams = {
    start_date: weekDays.start,
    end_date: weekDays.end,
    asset_ids: assetsIds,
    accommodation_ids: accommodationIds,
    organization_ids: organizationIds,
    filters: formValues?.entrance_quantity?.map(item => {
      return {
        condition: item.condition.name,
        value: Number(item.value),
      };
    }),
  };

  const { data: usersData, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/dashboard/users', {
        params: userParams,
      });
      return data;
    },
    queryKey: ['users-data', userParams],
    onError: error => {
      toastError(error);
    },
    enabled: !!startDate && !!endDate,
  });

  const { data: usersLoginData, isLoading: isUsersLoginDataLoading } = useQuery<
    IUsersLoginData | undefined
  >({
    queryFn: async () => {
      const { data } = await instance.get('admin/dashboard/user-logins', {
        params: loginActivityParams,
      });
      return data;
    },
    queryKey: ['user-logins', userParams, loginActivityParams],
    onError: error => {
      toastError(error);
    },
    enabled: !!startDate && !!endDate && dayjs(weekDays.start).isValid() && dayjs(weekDays.end).isValid(),
  });

  const userLoginDataArr = usersLoginData
    ? Object.entries(usersLoginData?.chart).filter(item => item[0] <= endDate)
    : [];

  const handleSubmit = (values: IFormikValues) => {
    setFormValues(values);
    setIsOpenDropdown(false);
  };

  const handleAddCondition = (
    values: IFormikValues,
    setFieldValue: FormikHelpers<IFormikValues>['setFieldValue']
  ) => {
    const updated = [
      ...values.entrance_quantity,
      { id: uuidv4(), condition: '', value: '' },
    ];
    setFieldValue('entrance_quantity', updated);
  };

  const handleDeleteCondition = (
    removeId: string,
    values: IFormikValues,
    setFieldValue: FormikHelpers<IFormikValues>['setFieldValue']
  ) => {
    const updated = values.entrance_quantity.filter(
      item => item.id !== removeId
    );
    setFieldValue('entrance_quantity', updated);
  };

  const deleteItem = (removeId: string) => {
    setFormValues(prev => ({
      ...prev,
      entrance_quantity: prev.entrance_quantity.filter(
        item => item.id !== removeId
      ),
    }));
  };

  useEffect(() => {
    setIsOpenDropdown(false);
    setWeekDays({
      start: dayjs(startDate).format('YYYY-MM-DD'),
      end: dayjs(startDate).add(1, 'week').format('YYYY-MM-DD'),
    });
  }, [startDate, endDate]);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.employeesNumber}>
        {isLoading ? (
          <>
            <div className={styles.numbersWrapper}>
              <div className={styles.shimmerBlock} />
            </div>
            <div className={styles.numbersWrapper}>
              <div className={styles.shimmerBlock} />
            </div>
            <div className={styles.numbersWrapper}>
              <div className={styles.shimmerBlock} />
            </div>
          </>
        ) : (
          <>
            <div className={`${styles.numbersWrapper} ${styles.fadeIn}`}>
              <span className={styles.number}>{usersData?.new_users}</span>
              <span className={styles.title}>{t('number_new_users')}</span>
            </div>
            <div className={`${styles.numbersWrapper} ${styles.fadeIn}`}>
              <span className={styles.number}>{usersData?.return_users}</span>
              <span className={styles.title}>{t('number_returning_users')}</span>
            </div>
            <div className={`${styles.numbersWrapper} ${styles.fadeIn}`}>
              <span className={styles.number}>{usersData?.with_business_trip}</span>
              <span className={styles.title}>{t('number_users_active_business_trips')}</span>
            </div>
          </>
        )}
      </div>
      <div className={styles.scheduleWrapper}>
        <DashboardBlock
          title={t('user_activity')}
          blockClassName={styles.block}
          actions={
            <NewDropdown
              isOpen={isOpenDropdown}
              setIsOpen={setIsOpenDropdown}
              title={t('entrance_quantity')}
            >
              <UserActivityParams
                onClick={() => setIsOpenDropdown(!isOpenDropdown)}
                initialValues={formValues}
                onSubmit={handleSubmit}
                handleAddCondition={handleAddCondition}
                handleDeleteCondition={handleDeleteCondition}
              />
            </NewDropdown>
          }
        >
          {!usersLoginData || isUsersLoginDataLoading ? (
            <div className={styles.shimmerChart}>
              <div className={styles.shimmerPillRow}>
                <div className={styles.shimmerPill} />
              </div>
              <div className={styles.shimmerBlock} />
              <div className={styles.shimmerDates}>
                {Array.from({ length: 7 }, (_, i) => (
                  <span key={i} className={styles.shimmerDate}>
                    {dayjs(weekDays.start).add(i, 'day').format('DD.MM')}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className={`${styles.chartWrapper} ${styles.fadeIn}`}>
              <div className={styles.itemsBox}>
                <div className={styles.itemsWrapper}>
                  {formValues.entrance_quantity.map(item => {
                    let value;

                    if (item.condition.name === '=') {
                      value = `${item.value} ${
                        Number(item.value) > 1
                          ? t('times').toLowerCase()
                          : t('once').toLowerCase()
                      }`;
                    } else if (item.condition.name === '>') {
                      value = `${t('more')} ${item.value} ${
                        Number(item.value) > 1
                          ? t('times').toLowerCase()
                          : t('once').toLowerCase()
                      }`;
                    } else if (item.condition.name === '<') {
                      value = `${t('less')} ${item.value} ${
                        Number(item.value) > 1
                          ? t('times').toLowerCase()
                          : t('once').toLowerCase()
                      }`;
                    } else if (item.condition.name === '>=') {
                      value = `${item.value} ${t('and_more')} ${t(
                        'once'
                      ).toLowerCase()}`;
                    } else if (item.condition.name === '<=') {
                      value = `${item.value} ${t('and_less')} ${t(
                        'once'
                      ).toLowerCase()}`;
                    }

                    return (
                      <div
                        className={styles.item}
                        key={item.id}
                        onMouseEnter={() => {
                          setTotalLogin(
                            usersLoginData?.total[
                              `${item.condition.name}${item.value}`
                            ] ?? null
                          );
                          setIsModalOpen(true);
                        }}
                        onMouseLeave={() => {
                          setIsModalOpen(false);
                          setTotalLogin(null);
                        }}
                      >
                        <span className={styles.itemTitle}>{value}</span>
                        <button
                          type="button"
                          className={styles.itemBtn}
                          onClick={() => deleteItem(item.id)}
                        >
                          <CloseItemIcon className={styles.itemIcon} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              {isModalOpen && (
                <div className={styles.totalLogin}>
                  <p>
                    {t('total_user_logins')}
                    {':'}
                  </p>
                  <span>{totalLogin}</span>
                </div>
              )}
              <UserLineActivityChart
                isUsersLoginDataLoading={isUsersLoginDataLoading}
                handleWeekChange={handleWeekChange}
                data={userLoginDataArr}
                hasFilters={!!formValues.entrance_quantity.length}
                formValues={formValues?.entrance_quantity || []}
                isPrevDisabled={!canGoPrev}
                isNextDisabled={!canGoNext}
              />
            </div>
          )}
        </DashboardBlock>
      </div>
    </div>
  );
};

export default EmployeesDashboard;
